import { Flex, Grid, InputText, Span } from "@goodfill/ui-components";
import { Link } from "react-router-dom";
import { Controller, DefaultValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRecoilState } from "recoil";
import { FieldGroup } from "../../../form/FieldGroup";
import { route } from "../../../routes";
import { ValidSignUpFromStateType, validSignUpFromState, signUpFromStateSchema } from "./signUpFromState";
import { StepSubmit } from "../../account/customerApplication/form/steps/StepSubmit";
import { PhoneInput } from "../../phoneInput/PhoneInput";
import { FormValidationResult } from "../../../form/formValidation";
import { useEffect } from "react";
import { deserializePhone, serializePhone } from "../../../form/zod/formPhone";
import { Popover } from "../../../ui-components/tooltip/Popover";

const PRIVACY_POLICY_LINK = import.meta.env.VITE_PRIVACY_POLICY_LINK;
const AGREEMENT_LINK = import.meta.env.VITE_AGREEMENT_LINK;

interface Props {
  defaultStep1State: DefaultValues<ValidSignUpFromStateType>;
  onSuccess: (data: ValidSignUpFromStateType) => void;
  userNameErrors: FormValidationResult;
  passwordErrors: FormValidationResult;
  isLoading: boolean;
}

export const SignUpForm: React.FC<Props> = ({
  defaultStep1State,
  onSuccess,
  userNameErrors,
  passwordErrors,
  isLoading,
}) => {
  const [, setValidSignUpFromState] = useRecoilState(validSignUpFromState);

  const { control, trigger, handleSubmit, setError } = useForm<ValidSignUpFromStateType>({
    defaultValues: defaultStep1State,
    mode: "onTouched",
    resolver: zodResolver(signUpFromStateSchema),
  });

  useEffect(() => {
    setError("email", { message: userNameErrors.join(", ") });
  }, [userNameErrors, setError]);

  useEffect(() => {
    setError("password", { message: passwordErrors.join(", ") });
  }, [passwordErrors, setError]);

  const onSubmit = async () => {
    const isStepValid = await trigger(undefined, { shouldFocus: true });

    if (isStepValid) {
      handleSubmit((validState) => {
        setValidSignUpFromState(validState);
        onSuccess(validState);
      })();
    }
  };

  return (
    <>
      <Flex direction="col" horizontalPadding={8} verticalPadding={8} borderBottom="thin" items="center">
        <Span size="2xl" uppercase={false} font="semibold">
          Hi Welcome!
        </Span>
        <Span className="Steps_welcomeMessage" wrap={true}>
          To open a free account and start you investing safely, we need to know more about you. Let's get started!
        </Span>

        <Grid cols={2} gap={0.75} className="w-full">
          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup
                label="First Name"
                validation={[error?.message ?? ""]}
                className="w-80-sm w-full-after-md"
                fill
              >
                <InputText
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder="Your First Name"
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"firstName"}
                  disabled={isLoading}
                />
              </FieldGroup>
            )}
            name="firstName"
            control={control}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup
                label="Last Name"
                validation={[error?.message ?? ""]}
                className="w-80-sm w-full-after-md"
                fill
              >
                <InputText
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder="Your Last Name"
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"lastName"}
                  disabled={isLoading}
                />
              </FieldGroup>
            )}
            name="lastName"
            control={control}
          />

          <Controller
            render={({ field, fieldState: { error } }) => {
              const [countryCallCode, nationalNumber] = deserializePhone(field.value);

              return (
                <FieldGroup
                  label="Phone Number"
                  validation={[error?.message ?? ""]}
                  className="w-80-sm w-full-after-md"
                  fill
                >
                  <PhoneInput
                    onChange={(value) => field.onChange(serializePhone(value.countryCallCode, value.nationalNumber))}
                    onBlur={field.onBlur}
                    isValid={error === undefined}
                    value={{ countryCallCode, nationalNumber }}
                    ref={field.ref}
                    id={"phone1"}
                    disabled={isLoading}
                  />
                </FieldGroup>
              );
            }}
            name="phone1"
            control={control}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup label="Email" validation={[error?.message ?? ""]} className="w-80-sm w-full-after-md" fill>
                <InputText
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder="Your email address"
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"email"}
                  disabled={isLoading}
                />
              </FieldGroup>
            )}
            name="email"
            control={control}
          />
          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup
                label="Password"
                validation={[error?.message ?? ""]}
                className="w-80-sm w-full-after-md"
                fill
                popoverDiv={
                  <Popover
                    content={
                      <div>
                        <p className="mb-0-important px-4 py-4 text-white">
                          Password should be of 8 characters.
                          <br />
                          At least one lowercase character is required.
                          <br />
                          At least one uppercase character is required.
                          <br />
                          At least one special character is required.
                          <br />
                        </p>
                      </div>
                    }
                    verticalOffset="3px"
                  />
                }
              >
                <InputText
                  type="password"
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder=""
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"password"}
                  disabled={isLoading}
                />
              </FieldGroup>
            )}
            name="password"
            control={control}
          />

          <Controller
            render={({ field, fieldState: { error } }) => (
              <FieldGroup
                label="Confirm Password"
                validation={[error?.message ?? ""]}
                className="w-80-sm w-full-after-md"
                fill
              >
                <InputText
                  type="password"
                  setValue={field.onChange}
                  border={error === undefined ? "neutral" : "red"}
                  placeholder=""
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  id={"passwordConfirmation"}
                  disabled={isLoading}
                />
              </FieldGroup>
            )}
            name="passwordConfirmation"
            control={control}
          />
        </Grid>

        <>
          <Span className="Steps_welcomeMessage" wrap={true}>
            Already started?
            <br />
            <Link to={route.signIn()}>Log in to complete your application</Link>
          </Span>
          <Span className="Steps_welcomeMessage" wrap={true}>
            By continuing, you agree to the{" "}
            <a target={"_blank"} rel="noreferrer" href={AGREEMENT_LINK} className="underline">
              Goodfill Customer Agreement,
              <br /> IB Client Agreements
            </a>{" "}
            <br />
            <a target={"_blank"} rel="noreferrer" href={PRIVACY_POLICY_LINK} className="underline">
              Privacy Policy
            </a>
          </Span>
        </>
      </Flex>

      <StepSubmit text="Continue" isLoading={isLoading} onClick={onSubmit} />
    </>
  );
};
